import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { withRouter } from '../../common/withRouter';
import {
  ButtonOBSOLETED,
  Notification,
  Message,
} from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";

// Sections under "Proxies" in the API appsettings holding each provider's credentials.
const credentialsKeyOptions = [
  { value: "Webshare", label: "Webshare (datacenter)" },
  { value: "WebshareIsp", label: "WebshareIsp (ISP)" },
  { value: "SmartProxy", label: "SmartProxy (Decodo ISP)" },
];

const emptyForm = {
  name: "",
  description: "",
  host: "",
  port: "",
  isRotating: false,
  credentialsKey: credentialsKeyOptions[0].value,
};

const groupBySystemType = (usages) =>
  usages.reduce((groups, usage) => ({
    ...groups,
    [usage.systemType]: [...(groups[usage.systemType] || []), usage],
  }), {});

// Only fixed Webshare ISP proxies can get a new IP: the API refuses the others too.
const isReplaceable = (proxy) => !proxy.isRotating && proxy.credentialsKey === "WebshareIsp";

// A refused (409) or failed (502) replacement explains why in its own body.
const replacementError = (e, fallback) => e?.payload?.responseData?.message || fallback;

// The Assign and Replace panels sit at the top of the page, like the form: opened from a row far
// down the list they looked like nothing had happened. Brings the panel into view when `key`
// (the proxy it opened for) changes to a value.
const useScrollIntoViewWhenOpened = (key) => {
  const ref = useRef(null);

  useEffect(() => {
    if (key == null || !ref.current) return;
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [key]);

  return ref;
};

const notOnWebshareRowStyle = { backgroundColor: "#FDECEC" };
const notOnWebshareTextStyle = { color: "#8A1C1C", fontWeight: "bold", marginTop: "0.3rem" };

const ProxiesInternal = ({ api }) => {
  const [proxies, setProxies] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [systemTypeOptions, setSystemTypeOptions] = useState([]);
  const [assigningProxy, setAssigningProxy] = useState(null);
  const [assignSystemType, setAssignSystemType] = useState(null);
  // { proxy, check: dry run result, pending: replacement Webshare had not finished yet, busy }
  const [replacing, setReplacing] = useState(null);
  // Ids of the fixed Webshare rows whose IP is in no active Webshare plan anymore (replaced
  // without updating the row, e.g. a replacement never completed): their integrations fail.
  const [notOnWebshareIds, setNotOnWebshareIds] = useState(new Set());
  const [isWebshareCheckUnavailable, setIsWebshareCheckUnavailable] = useState(false);
  const [notification, setNotification] = useState(null);
  const assignPanelRef = useScrollIntoViewWhenOpened(assigningProxy?.id);
  const replacePanelRef = useScrollIntoViewWhenOpened(replacing?.proxy.id);

  // The form lives at the top of the page, so opening it from a row far down the
  // list looked like nothing had happened. Bringing it into view — and putting the
  // cursor in it — is what says "it opened". Counting the openings instead of
  // watching `isFormVisible` also covers clicking Edit on a second row while the
  // form is already open, which changes no visible state of its own.
  const formRef = useRef(null);
  const [formOpenings, setFormOpenings] = useState(0);

  useEffect(() => {
    if (!formOpenings || !formRef.current) return;
    formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    const first = formRef.current.querySelector("input, select, textarea");
    if (first) first.focus({ preventScroll: true });
  }, [formOpenings]);

  const rowStyle = { display: "table", width: "100%", marginBottom: "0" };
  const labelStyle = { display: "table-cell", width: "30%", fontWeight: "bold", verticalAlign: "top" };
  const fieldStyle = { display: "table-cell" };
  const containerStyle = { width: "80%", margin: "auto", backgroundColor: "white", border: "1px solid #e5e5e5", padding: "1.2rem" };

  // Asks Webshare which IPs we still have. Kept apart from the list on purpose: when Webshare is
  // slow or down the page still shows the proxies, only without the check.
  const checkWebshare = async () => {
    let webshare;
    try {
      webshare = await api.proxies.getWebshareProxies();
    } catch (e) {
      setIsWebshareCheckUnavailable(true);
      return;
    }

    if (!webshare) {
      return;
    }

    setIsWebshareCheckUnavailable(false);
    setNotOnWebshareIds(new Set(webshare.rowsNotOnWebshare.map(({ id }) => id)));
  };

  const loadData = async () => {
    checkWebshare();
    setProxies(await api.proxies.getProxies());
  };

  useEffect(() => {
    loadData();
    api.integrations.getSystemTypes().then((systemTypes) =>
      setSystemTypeOptions(systemTypes.map(({ Name }) => ({ value: Name, label: Name })))
    );
  }, []);

  const startAssign = (proxy) => {
    setAssigningProxy(proxy);
    setAssignSystemType(null);
  };

  const assign = async () => {
    if (!confirm(`Set the proxy ${assigningProxy.name} on every ${assignSystemType.value} integration?`)) {
      return;
    }

    let updated;
    try {
      updated = await api.proxies.assignProxyToSystemType(assigningProxy.id, assignSystemType.value);
    } catch (e) {
      setNotification(new Message("error", "Unable to assign the proxy."));
      return;
    }

    setNotification(new Message("success", `Proxy ${assigningProxy.name} set on ${updated} ${assignSystemType.value} integration(s).`));
    setAssigningProxy(null);
    loadData();
  };

  // Asks Webshare to validate the replacement first (dry run), so the admin confirms knowing
  // that it can be done and how many replacements the plan has left.
  const startReplace = async (proxy) => {
    setAssigningProxy(null);
    setReplacing({ proxy, check: null, pending: null, busy: true });

    let check;
    try {
      check = await api.proxies.replaceProxy(proxy.id, true);
    } catch (e) {
      setReplacing(null);
      setNotification(new Message("error", replacementError(e, "Unable to check the replacement.")));
      return;
    }

    if (check.status !== "DryRunOk") {
      setReplacing(null);
      setNotification(new Message("warning", "Webshare is still validating the replacement: try again in a moment."));
      return;
    }

    setReplacing({ proxy, check, pending: null, busy: false });
  };

  const finishReplace = (result) => {
    setReplacing(null);
    const text = `${result.oldHost} replaced with ${result.newHost}.`;
    setNotification(result.warnings.length > 0
      ? new Message("warning", `${text} ${result.warnings.join(" ")}`)
      : new Message("success", text));
    loadData();
  };

  const replace = async () => {
    setReplacing({ ...replacing, busy: true });

    let result;
    try {
      result = await api.proxies.replaceProxy(replacing.proxy.id, false);
    } catch (e) {
      setReplacing({ ...replacing, busy: false });
      setNotification(new Message("error", replacementError(e, "Unable to replace the IP.")));
      return;
    }

    if (result.status === "Pending") {
      setReplacing({ ...replacing, pending: result, busy: false });
      return;
    }

    finishReplace(result);
  };

  // Webshare finished a replacement after the API stopped waiting: until this runs, the row
  // still points to the old IP, which is no longer ours.
  const completeReplace = async () => {
    const { proxy, pending } = replacing;
    setReplacing({ ...replacing, busy: true });

    let result;
    try {
      result = await api.proxies.completeProxyReplacement(proxy.id, pending.planId, pending.replacementId);
    } catch (e) {
      setReplacing({ ...replacing, busy: false });
      setNotification(new Message("error", replacementError(e, "Unable to complete the replacement.")));
      return;
    }

    if (result.status === "Pending") {
      setReplacing({ ...replacing, busy: false });
      setNotification(new Message("warning", "Webshare is still processing the replacement: try again in a minute."));
      return;
    }

    finishReplace(result);
  };

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsFormVisible(true);
    setFormOpenings((count) => count + 1);
  };

  const startEdit = (proxy) => {
    setIsFormVisible(true);
    setFormOpenings((count) => count + 1);
    setEditingId(proxy.id);
    setForm({
      name: proxy.name,
      description: proxy.description || "",
      host: proxy.host || "",
      port: proxy.port || "",
      isRotating: proxy.isRotating,
      credentialsKey: proxy.credentialsKey,
    });
  };

  const resetForm = () => {
    setIsFormVisible(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    const data = { ...form, port: form.port === "" ? null : Number(form.port) };

    try {
      if (editingId) {
        await api.proxies.updateProxy(editingId, data);
      } else {
        await api.proxies.addProxy(data);
      }
    } catch (e) {
      setNotification(new Message("error", "Unable to save the proxy."));
      return;
    }

    setNotification(new Message("success", "Proxy saved."));
    resetForm();
    loadData();
  };

  const remove = async (proxy) => {
    if (!confirm(`Are you sure to delete the proxy ${proxy.name}?`)) {
      return;
    }

    try {
      await api.proxies.deleteProxy(proxy.id);
    } catch (e) {
      setNotification(new Message("error", "Unable to delete the proxy."));
      return;
    }

    loadData();
  };

  const input = (name, placeholder) => (
    <input
      name={name}
      value={form[name]}
      placeholder={placeholder}
      onChange={({ target }) => setForm({ ...form, [name]: target.value })}
    />
  );

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Proxies" documentDescription="Proxies" />
            <h2 className="main-page-header">
              Proxies
              <span className="add-entity-button" style={{ cursor: "pointer" }} onClick={startNew} />
            </h2>

            {isFormVisible && (
              <div style={containerStyle} ref={formRef}>
                <h3>{editingId ? "Edit proxy" : "New proxy"}</h3>
                <div style={rowStyle}>
                  <label style={labelStyle}>Name</label>
                  <span style={fieldStyle}>{input("name", "name")}</span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label style={labelStyle}>Description</label>
                  <span style={fieldStyle}>{input("description", "description")}</span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label style={labelStyle}>Rotating</label>
                  <span style={fieldStyle}>
                    <input
                      type="checkbox"
                      checked={form.isRotating}
                      onChange={({ target }) => setForm({ ...form, isRotating: target.checked })}
                    />
                  </span>
                </div>
                <hr />
                {!form.isRotating && (
                  <>
                    <div style={rowStyle}>
                      <label style={labelStyle}>Host</label>
                      <span style={fieldStyle}>{input("host", "IP or host name")}</span>
                    </div>
                    <hr />
                    <div style={rowStyle}>
                      <label style={labelStyle}>Port</label>
                      <span style={fieldStyle}>{input("port", "port")}</span>
                    </div>
                    <hr />
                  </>
                )}
                <div style={rowStyle}>
                  <label style={labelStyle}>Credentials</label>
                  <span style={fieldStyle}>
                    <ReactSelect
                      className="react-select-filters-section"
                      classNamePrefix="react-select-filters-section-prefix"
                      value={credentialsKeyOptions.find(({ value }) => value === form.credentialsKey)}
                      onChange={({ value }) => setForm({ ...form, credentialsKey: value })}
                      options={credentialsKeyOptions}
                    />
                  </span>
                </div>
                <br />
                <div style={{ justifyContent: "center", display: "flex", gap: "1.5rem" }}>
                  <ButtonOBSOLETED type="submit" buttonContent="Save" onClick={save} />
                  <ButtonOBSOLETED type="reset" buttonContent="Cancel" onClick={resetForm} />
                </div>
              </div>
            )}

            {assigningProxy && (
              <div style={containerStyle} ref={assignPanelRef}>
                <h3>Assign {assigningProxy.name} to every integration of a system type</h3>
                <div style={rowStyle}>
                  <label style={labelStyle}>System type</label>
                  <span style={fieldStyle}>
                    <ReactSelect
                      className="react-select-filters-section"
                      classNamePrefix="react-select-filters-section-prefix"
                      value={assignSystemType}
                      onChange={setAssignSystemType}
                      options={systemTypeOptions}
                    />
                  </span>
                </div>
                <br />
                <div style={{ justifyContent: "center", display: "flex", gap: "1.5rem" }}>
                  <ButtonOBSOLETED type="submit" buttonContent="Assign" disabled={!assignSystemType} onClick={assign} />
                  <ButtonOBSOLETED type="reset" buttonContent="Cancel" onClick={() => setAssigningProxy(null)} />
                </div>
              </div>
            )}

            {replacing && (
              <div style={containerStyle} ref={replacePanelRef}>
                <h3>Replace the IP of {replacing.proxy.name}</h3>
                {!replacing.check ? (
                  <span>Asking Webshare...</span>
                ) : replacing.pending ? (
                  <>
                    <p>
                      Webshare is still processing replacement #{replacing.pending.replacementId}. The proxy
                      still points to {replacing.proxy.host}:{replacing.proxy.port}, which will stop working:
                      wait a minute and click Complete to point it to the new IP. Do not leave the page before.
                    </p>
                    <div style={{ justifyContent: "center", display: "flex", gap: "1.5rem" }}>
                      <ButtonOBSOLETED type="submit" buttonContent="Complete" disabled={replacing.busy} onClick={completeReplace} />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={rowStyle}>
                      <label style={labelStyle}>Current IP</label>
                      <span style={fieldStyle}>{replacing.proxy.host}:{replacing.proxy.port} (goes back to Webshare, it cannot be recovered)</span>
                    </div>
                    <hr />
                    <div style={rowStyle}>
                      <label style={labelStyle}>New IP</label>
                      <span style={fieldStyle}>picked by Webshare, country {replacing.check.countryCode}</span>
                    </div>
                    <hr />
                    <div style={rowStyle}>
                      <label style={labelStyle}>Replacements left</label>
                      <span style={fieldStyle}>{replacing.check.replacementsAvailableBefore} (this uses one)</span>
                    </div>
                    <hr />
                    <div style={rowStyle}>
                      <label style={labelStyle}>Integrations</label>
                      <span style={fieldStyle}>{replacing.proxy.usedBy.length} will follow the new IP</span>
                    </div>
                    <br />
                    {replacing.busy && <p>Replacing... Webshare can take up to a minute and a half, keep this page open.</p>}
                    <div style={{ justifyContent: "center", display: "flex", gap: "1.5rem" }}>
                      <ButtonOBSOLETED type="submit" buttonContent="Replace" disabled={replacing.busy} onClick={replace} />
                      <ButtonOBSOLETED type="reset" buttonContent="Cancel" disabled={replacing.busy} onClick={() => setReplacing(null)} />
                    </div>
                  </>
                )}
              </div>
            )}

            <br />
            {isWebshareCheckUnavailable && (
              <p>Webshare check unavailable: rows whose IP is no longer on Webshare are not highlighted.</p>
            )}
            {proxies == null ? (
              <span>Loading...</span>
            ) : (
              <article className="data-table-wrap">
                <table className="data-lists component-data-table cell-content-align-top">
                  <thead>
                    <tr>
                      <td>Name</td>
                      <td>Address</td>
                      <td>Credentials</td>
                      <td>Used by</td>
                      <td>Action</td>
                    </tr>
                  </thead>
                  <tbody>
                    {proxies.map((proxy) => (
                      <tr key={proxy.id} style={notOnWebshareIds.has(proxy.id) ? notOnWebshareRowStyle : undefined}>
                        <td>
                          <strong>{proxy.name}</strong>
                          {proxy.description && <div>{proxy.description}</div>}
                          {notOnWebshareIds.has(proxy.id) && (
                            <div style={notOnWebshareTextStyle}>
                              Not on Webshare anymore: this IP is in no active plan, the integrations using it fail.
                              Fix host and port by hand (Edit).
                            </div>
                          )}
                        </td>
                        <td>{proxy.isRotating ? "rotating" : `${proxy.host}:${proxy.port}`}</td>
                        <td>{proxy.credentialsKey}</td>
                        <td>
                          {proxy.usedBy.length === 0 ? "-" : (
                            // One collapsed line per system type: a proxy set on a whole
                            // provider can be used by hundreds of integrations.
                            Object.entries(groupBySystemType(proxy.usedBy)).map(([systemType, usages]) => (
                              <details key={systemType}>
                                <summary>{systemType} ({usages.length})</summary>
                                <ul>
                                  {usages.map((usage) => (
                                    <li key={usage.integrationId}>
                                      <a href={`/product/${usage.productId}/integration`}>
                                        {usage.facilityName} - {usage.productName}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            ))
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem", whiteSpace: "nowrap" }}>
                            <ButtonOBSOLETED buttonContent="Edit" onClick={() => startEdit(proxy)} disabled={false} />
                            <ButtonOBSOLETED buttonContent="Assign" onClick={() => startAssign(proxy)} disabled={false} />
                            {isReplaceable(proxy) && (
                              <ButtonOBSOLETED buttonContent="Replace IP" onClick={() => startReplace(proxy)} disabled={!!replacing} />
                            )}
                            <ButtonOBSOLETED
                              buttonContent="Delete"
                              onClick={() => remove(proxy)}
                              disabled={proxy.usedBy.length > 0}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const Proxies = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(ProxiesInternal)
  )
);

Proxies.requiresLogin = true;
Proxies.propTypes = { isLoggedIn: PropTypes.bool };

export default Proxies;
